import { useState } from "react";
import {
  Drawer, Tabs, Tag, Spin, Empty, Timeline, Form, Input, Button,
  message, Tooltip, Modal, Image, Select, Popconfirm, Upload, DatePicker,
} from "antd";
import {
  Camera, Clock, CheckSquare, Package, FileCheck2, MapPin, User, Calendar,
  MessageSquare, PlayCircle, StopCircle, Trash2, UserCheck, PlusCircle, Pencil,
} from "lucide-react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { IJob, TJobStatus } from "../../../types/api/job";
import { JOB_STATUS_CFG } from "../../../constants/jobStatusConfig";
import {
  useJobPhotos, useJobTimeLogs, useJobChecklistResponses,
  useJobParts, useJobSignOff, useSaveJobSignOff,
  useJobClockIn, useJobClockOut, useJobTimeLogUpdate,
  useJobNotes, useJobAddNote, useJobDeleteNote,
} from "../../../hooks/api/jobs/job-detail-hooks";
import { useJobUpdate } from "../../../hooks/api/jobs/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";
import { useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../../lib/axios";
import { IJobPhoto, IJobChecklistResponse } from "../../../types/api/incident";

dayjs.extend(duration);

const STATUS_CFG: Record<TJobStatus, { cls: string; label: string }> = Object.fromEntries(
  Object.entries(JOB_STATUS_CFG).map(([k, v]) => [k, { cls: `${v.bg} ${v.text}`, label: v.label }])
) as Record<TJobStatus, { cls: string; label: string }>;

function getDisplayStatus(job: IJob): { cls: string; label: string } {
  const now = dayjs();
  if (job.scheduled_end && dayjs(job.scheduled_end).isBefore(now)) {
    if (job.status === "scheduled" || job.status === "draft") {
      return { cls: "bg-orange-100 text-orange-700", label: "Expired" };
    }
    if (job.status === "in_progress") {
      return { cls: "bg-red-100 text-red-700", label: "Overdue" };
    }
  }
  return STATUS_CFG[job.status] ?? { cls: "bg-slate-100 text-slate-600", label: job.status };
}

function fmtMins(mins: number | null | undefined) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewTab({ job }: { job: IJob }) {
  const [msgApi, ctx] = message.useMessage();
  const [assignModal, setAssignModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<number | null>(null);

  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 200, status: "ACTIVE" });
  const workerOptions = ((workerRes?.data as any[]) ?? []).map((w: any) => {
    const name = `${w.first_name ?? ""} ${w.last_name ?? ""}`.trim() || w.email || "—";
    return { value: w.id, label: w.employee_number ? `${name} (${w.employee_number})` : name };
  });

  const assignMutation = useJobUpdate({
    qData: {},
    onSuccess: () => {
      msgApi.success("Engineer assigned and notified.");
      setAssignModal(false);
      setSelectedWorker(null);
    },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Assignment failed"),
  });

  const steps = ["Travel", "Arrived", "On Site", "Evidence", "Sign Off"];
  const stepDone = job.status === "completed" ? 5
    : job.status === "in_progress" ? 3
    : job.status === "scheduled" ? 1
    : 0;

  return (
    <div className="space-y-5">
      {ctx}
      {/* journey stepper */}
      <div className="rounded-xl p-4" style={{ background: "rgba(1,59,190,0.04)", border: "1px solid rgba(1,59,190,0.1)" }}>
        <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--color-primary)" }}>Job Journey</p>
        <div className="flex items-center justify-between">
          {steps.map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 flex-1 relative">
              {i < steps.length - 1 && (
                <div className={`absolute top-[15px] left-[60%] right-[-40%] h-0.5`}
                  style={{ background: i < stepDone ? "var(--color-primary)" : "#e2e8f0" }} />
              )}
              <div className={`w-[30px] h-[30px] rounded-full flex items-center justify-center z-10 text-xs font-bold`}
                style={
                  i < stepDone
                    ? { background: "var(--color-primary)", color: "#fff" }
                    : i === stepDone
                    ? { background: "rgba(1,59,190,0.12)", color: "var(--color-primary)", outline: "2px solid rgba(1,59,190,0.35)", outlineOffset: "1px" }
                    : { background: "#f1f5f9", color: "#94a3b8" }
                }>
                {i < stepDone ? "✓" : i + 1}
              </div>
              <span className="text-[10px] font-medium text-center"
                style={{ color: i <= stepDone ? "var(--color-primary)" : "#94a3b8" }}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* key info */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-100 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-1"><User size={14} /> Client</div>
          <div className="text-sm font-medium text-slate-700 truncate">{job.client_name || "—"}</div>
        </div>
        {/* Worker with Assign & Notify */}
        <div className="bg-white border border-slate-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 text-slate-400 text-xs"><User size={14} /> Worker</div>
            <Tooltip title="Assign & notify engineer">
              <button
                onClick={() => { setSelectedWorker(job.assigned_user_id ?? null); setAssignModal(true); }}
                className="flex items-center gap-1 text-[10px] text-primary font-semibold hover:opacity-70"
              >
                <UserCheck size={11} /> Assign
              </button>
            </Tooltip>
          </div>
          <div className="text-sm font-medium text-slate-700 truncate">{job.assigned_user_name || "Unassigned"}</div>
        </div>
        {[
          ["Scheduled", job.scheduled_start ? dayjs(job.scheduled_start).format("DD MMM YYYY HH:mm") : "—", <Calendar size={14} />],
          ["Actual Start", job.actual_start ? dayjs(job.actual_start).format("DD MMM YYYY HH:mm") : "—", <Clock size={14} />],
          ["Actual End", job.actual_end ? dayjs(job.actual_end).format("DD MMM YYYY HH:mm") : "—", <Clock size={14} />],
          ["Site", job.site_name || "—", <MapPin size={14} />],
        ].map(([label, val, icon], i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-slate-400 text-xs mb-1">{icon as any} {label as string}</div>
            <div className="text-sm font-medium text-slate-700 truncate">{val as string}</div>
          </div>
        ))}
      </div>

      {job.description && (
        <div className="bg-white border border-slate-100 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Description</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{job.description}</p>
        </div>
      )}
      {job.notes && (
        <div className="bg-white border border-slate-100 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Internal Notes</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{job.notes}</p>
        </div>
      )}

      {/* ── Assign & Notify Modal ── */}
      <Modal
        open={assignModal}
        onCancel={() => setAssignModal(false)}
        title="Assign & Notify Engineer"
        width={380}
        footer={[
          <Button key="cancel" onClick={() => setAssignModal(false)}>Cancel</Button>,
          <Button
            key="assign"
            type="primary"
            loading={assignMutation.isPending}
            disabled={!selectedWorker}
            onClick={() => {
              if (!selectedWorker) return;
              assignMutation.mutate({ id: job.id, assigned_user_id: selectedWorker });
            }}
          >
            Assign & Notify
          </Button>,
        ]}
        destroyOnClose
      >
        <div className="py-3">
          <p className="text-sm text-slate-600 mb-3">Select engineer to assign to job <strong>{job.job_number}</strong>:</p>
          <Select
            showSearch
            className="w-full"
            placeholder="Select engineer…"
            value={selectedWorker}
            onChange={setSelectedWorker}
            options={workerOptions}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Modal>
    </div>
  );
}

// ── Photos ────────────────────────────────────────────────────────────────────

const PHOTO_TYPE_COLOR: Record<string, string> = {
  before: "blue", after: "green", progress: "orange", evidence: "purple", other: "default",
};

function PhotosTab({ jobId }: { jobId: number }) {
  const queryClient = useQueryClient();
  const { data: photos = [], isLoading, refetch } = useJobPhotos(jobId);
  const [activeType, setActiveType] = useState<string>("all");
  const [uploadType, setUploadType] = useState<string>("before");
  const [msgApi, ctx] = message.useMessage();

  const UPLOAD_TYPES = ["before", "after", "progress", "evidence", "other"];

  const types = ["all", ...Array.from(new Set(photos.map((p) => p.photo_type)))];
  const filtered = activeType === "all" ? photos : photos.filter((p) => p.photo_type === activeType);

  return (
    <div>
      {ctx}
      {/* Upload section */}
      <div className="mb-4 p-3 rounded-xl border border-slate-100 bg-slate-50">
        <p className="text-xs font-semibold text-slate-500 mb-2">Upload Photo</p>
        <div className="flex items-center gap-2 mb-2">
          <Select
            value={uploadType}
            onChange={setUploadType}
            size="small"
            className="w-32"
            options={UPLOAD_TYPES.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
          />
          <span className="text-xs text-slate-400">Photo type</span>
        </div>
        <Upload
          accept="image/*"
          showUploadList={false}
          customRequest={({ file, onSuccess, onError }: any) => {
            const fd = new FormData();
            fd.append("photo", file);
            fd.append("photo_type", uploadType);
            axiosInstance.post(`/jobs/${jobId}/photos`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            }).then((r) => {
              onSuccess?.(r);
              msgApi.success("Photo uploaded.");
              refetch();
              queryClient.invalidateQueries({ queryKey: ["job-photos", jobId] });
            }).catch((err) => {
              onError?.(err);
              msgApi.error(err?.response?.data?.message || "Upload failed");
            });
          }}
        >
          <Button size="small" icon={<PlusCircle size={12} />} className="!text-primary !border-primary">
            Choose & Upload
          </Button>
        </Upload>
      </div>

      {isLoading ? <div className="flex justify-center py-8"><Spin /></div>
        : !photos.length ? <Empty description="No photos uploaded yet" className="py-8" />
        : (
      <>
      <div className="flex gap-2 mb-4 flex-wrap">
        {types.map((t) => (
          <button key={t} onClick={() => setActiveType(t)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors`}
            style={activeType === t
              ? { background: "var(--color-primary)", color: "#fff", borderColor: "var(--color-primary)" }
              : { background: "#fff", color: "#64748b", borderColor: "#e2e8f0" }}>
            {t.charAt(0).toUpperCase() + t.slice(1)} {t === "all" ? `(${photos.length})` : `(${photos.filter((p) => p.photo_type === t).length})`}
          </button>
        ))}
      </div>
      <Image.PreviewGroup>
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((p: IJobPhoto) => (
            <div key={p.id} className="rounded-xl overflow-hidden border border-slate-100 relative group">
              <Image
                src={p.file_url}
                alt={p.caption || p.photo_type}
                className="w-full object-cover"
                style={{ height: 140 }}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
              />
              <div className="p-2 bg-white">
                <div className="flex items-center justify-between">
                  <Tag color={PHOTO_TYPE_COLOR[p.photo_type] ?? "default"} className="text-xs !m-0">{p.photo_type}</Tag>
                  <span className="text-[10px] text-slate-400">{dayjs(p.taken_at).format("DD MMM HH:mm")}</span>
                </div>
                {p.caption && <p className="text-xs text-slate-500 mt-1 truncate">{p.caption}</p>}
                {p.uploaded_by_name && <p className="text-[10px] text-slate-400">{p.uploaded_by_name}</p>}
              </div>
            </div>
          ))}
        </div>
      </Image.PreviewGroup>
      </>
      )}
    </div>
  );
}

// ── Checklist ─────────────────────────────────────────────────────────────────

function ChecklistTab({ jobId }: { jobId: number }) {
  const { data: items = [], isLoading } = useJobChecklistResponses(jobId);

  if (isLoading) return <div className="flex justify-center py-12"><Spin /></div>;
  if (!items.length) return <Empty description="No checklist responses yet" className="py-12" />;

  const grouped = items.reduce((acc: Record<string, IJobChecklistResponse[]>, i) => {
    const key = i.template_name || "Checklist";
    if (!acc[key]) acc[key] = [];
    acc[key].push(i);
    return acc;
  }, {});

  const passCount = items.filter((i) => i.response === "pass" || i.response_bool === true).length;
  const failCount = items.filter((i) => i.response === "fail" || i.response_bool === false).length;

  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-emerald-600">{passCount}</div>
          <div className="text-xs text-emerald-500">Pass</div>
        </div>
        <div className="flex-1 bg-red-50 border border-red-100 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-red-600">{failCount}</div>
          <div className="text-xs text-red-500">Fail</div>
        </div>
        <div className="flex-1 bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-slate-600">{items.length - passCount - failCount}</div>
          <div className="text-xs text-slate-400">Pending</div>
        </div>
      </div>
      {Object.entries(grouped).map(([name, groupItems]) => (
        <div key={name} className="mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{name}</p>
          <div className="space-y-2">
            {groupItems.map((item) => {
              const isPass = item.response === "pass" || item.response_bool === true;
              const isFail = item.response === "fail" || item.response_bool === false;
              return (
                <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border ${isFail ? "border-red-200 bg-red-50" : isPass ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-white"}`}>
                  <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold mt-0.5 ${isFail ? "bg-red-500 text-white" : isPass ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"}`}>
                    {isFail ? "✗" : isPass ? "✓" : "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">{item.item_text}</p>
                    {item.response && !["pass", "fail"].includes(item.response) && (
                      <p className="text-xs text-slate-500 mt-0.5">{item.response}</p>
                    )}
                    {item.fail_triggers_alert && isFail && (
                      <span className="text-xs text-red-600 font-medium">⚠ Alert triggered</span>
                    )}
                  </div>
                  {item.photo_url && (
                    <Image src={item.photo_url} width={48} height={48} className="rounded object-cover flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Time Logs ─────────────────────────────────────────────────────────────────

function TimeLogsTab({ jobId }: { jobId: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [editForm] = Form.useForm();
  const [editingLog, setEditingLog] = useState<any | null>(null);
  const { data: logs = [], isLoading, refetch } = useJobTimeLogs(jobId);

  const activeLog = logs.find((l: any) => !l.end_time);

  const clockIn = useJobClockIn({
    onSuccess: () => { msgApi.success("Clocked in."); refetch(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Clock in failed"),
  });

  const clockOut = useJobClockOut({
    onSuccess: () => { msgApi.success("Clocked out."); refetch(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Clock out failed"),
  });

  const updateLog = useJobTimeLogUpdate({
    jobId,
    onSuccess: () => { msgApi.success("Time log updated."); setEditingLog(null); refetch(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed to update time log"),
  });

  function openEdit(log: any) {
    setEditingLog(log);
    editForm.setFieldsValue({
      start_time: log.start_time ? dayjs(log.start_time) : null,
      end_time: log.end_time ? dayjs(log.end_time) : null,
      notes: log.notes || "",
    });
  }

  function submitEdit(vals: any) {
    updateLog.mutate({
      logId: editingLog.id,
      start_time: vals.start_time ? vals.start_time.toISOString() : undefined,
      end_time: vals.end_time ? vals.end_time.toISOString() : undefined,
      notes: vals.notes,
    });
  }

  if (isLoading) return <div className="flex justify-center py-12"><Spin /></div>;

  const totalLabour = logs.reduce((s: number, l: any) => s + (l.duration_mins || 0), 0);
  const totalTravel = logs.reduce((s: number, l: any) => s + (l.travel_mins || 0), 0);

  return (
    <div>
      {ctx}
      {/* Clock in/out control */}
      <div className="mb-4 p-3 rounded-xl border" style={activeLog
        ? { background: "rgba(16,185,129,0.06)", borderColor: "rgba(16,185,129,0.3)" }
        : { background: "rgba(1,59,190,0.04)", borderColor: "rgba(1,59,190,0.14)" }
      }>
        {activeLog ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />
                Clocked in
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Since {dayjs((activeLog as any).start_time).format("HH:mm")}</div>
            </div>
            <Button
              danger
              size="small"
              icon={<StopCircle size={12} />}
              loading={clockOut.isPending}
              onClick={() => clockOut.mutate({ jobId, logId: (activeLog as any).id })}
            >
              Clock Out
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Not clocked in</span>
            <Button
              size="small"
              type="primary"
              icon={<PlayCircle size={12} />}
              loading={clockIn.isPending}
              onClick={() => clockIn.mutate({ jobId })}
            >
              Clock In
            </Button>
          </div>
        )}
      </div>

      {!logs.length ? <Empty description="No time logs yet" className="py-8" /> : (
        <>
          <div className="flex gap-3 mb-4">
            <div className="flex-1 rounded-lg p-3 text-center" style={{ background: "rgba(1,59,190,0.07)", border: "1px solid rgba(1,59,190,0.14)" }}>
              <div className="text-lg font-bold text-primary">{fmtMins(totalLabour)}</div>
              <div className="text-xs text-primary/70">Labour</div>
            </div>
            <div className="flex-1 bg-teal-50 border border-teal-100 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-teal-700">{fmtMins(totalTravel)}</div>
              <div className="text-xs text-teal-500">Travel</div>
            </div>
          </div>
          <Timeline
            items={logs.map((l: any) => ({
              color: l.end_time ? (l.is_billable ? "blue" : "gray") : "green",
              children: (
                <div className="bg-white border border-slate-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-700">{l.worker_name || "Worker"}</span>
                    <div className="flex items-center gap-1">
                      {!l.end_time && <Tag color="green" className="text-xs">Active</Tag>}
                      {l.is_billable && <Tag color="blue" className="text-xs">Billable</Tag>}
                      <Tooltip title="Correct clock-in/out times">
                        <Button
                          type="text"
                          size="small"
                          icon={<Pencil size={12} className="text-slate-400" />}
                          onClick={() => openEdit(l)}
                        />
                      </Tooltip>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">
                    {dayjs(l.start_time).format("DD MMM HH:mm")}
                    {l.end_time && ` → ${dayjs(l.end_time).format("HH:mm")}`}
                  </div>
                  <div className="flex gap-3 mt-1 text-xs text-slate-400">
                    {l.duration_mins != null && <span>Labour: {fmtMins(l.duration_mins)}</span>}
                    {l.travel_mins > 0 && <span>Travel: {fmtMins(l.travel_mins)}</span>}
                    {l.hourly_rate && <span>£{Number(l.hourly_rate).toFixed(2)}/hr</span>}
                  </div>
                  {l.notes && <div className="text-xs text-slate-400 mt-1 italic">{l.notes}</div>}
                </div>
              ),
            }))}
          />
        </>
      )}

      {/* Edit time log modal — office correction of a worker's clock-in/out */}
      <Modal
        open={!!editingLog}
        onCancel={() => setEditingLog(null)}
        title="Correct Clock-In / Clock-Out"
        onOk={() => editForm.submit()}
        okText="Save Correction"
        confirmLoading={updateLog.isPending}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" size="middle" className="pt-2" onFinish={submitEdit}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="start_time"
              label="Clock In"
              rules={[{ required: true, message: "Clock in time is required" }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="Clock Out"
              dependencies={["start_time"]}
              rules={[
                {
                  validator: (_rule, value) => {
                    const start = editForm.getFieldValue("start_time");
                    if (value && start && value.valueOf() <= start.valueOf()) {
                      return Promise.reject(new Error("Clock out must be after clock in"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" className="w-full" />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Reason for correction (optional)…" />
          </Form.Item>
          <p className="text-xs text-slate-400">
            This correction is recorded in the audit log with your name and the previous values.
          </p>
        </Form>
      </Modal>
    </div>
  );
}

// ── Notes ─────────────────────────────────────────────────────────────────────

function NotesTab({ jobId }: { jobId: number }) {
  const [visFilter, setVisFilter] = useState<string>("all");
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: notes = [], isLoading } = useJobNotes(jobId, visFilter);

  const addNote = useJobAddNote({
    jobId,
    onSuccess: () => { msgApi.success("Note added."); form.resetFields(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed to add note"),
  });

  const delNote = useJobDeleteNote({
    jobId,
    onSuccess: () => msgApi.success("Note deleted."),
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed to delete note"),
  });

  return (
    <div>
      {ctx}
      {/* Add note form */}
      <Form form={form} layout="vertical" size="middle" className="mb-5"
        onFinish={(vals) => addNote.mutate({ note: vals.note, visibility: vals.visibility })}>
        <Form.Item name="note" rules={[{ required: true, message: "Note is required" }]}>
          <Input.TextArea rows={3} placeholder="Write a note…" />
        </Form.Item>
        <div className="flex gap-2">
          <Form.Item name="visibility" initialValue="internal" className="mb-0 flex-1">
            <Select options={[
              { value: "internal", label: "Internal (not visible to client)" },
              { value: "client", label: "Client visible" },
            ]} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={addNote.isPending}>
            Add Note
          </Button>
        </div>
      </Form>

      {/* Filter */}
      <div className="flex gap-2 mb-3">
        {["all", "internal", "client"].map((v) => (
          <button key={v} onClick={() => setVisFilter(v)}
            className="px-3 py-1 rounded-full text-xs font-semibold border transition-colors capitalize"
            style={visFilter === v
              ? { background: "var(--color-primary)", color: "#fff", borderColor: "var(--color-primary)" }
              : { background: "#fff", color: "#64748b", borderColor: "#e2e8f0" }}>
            {v}
          </button>
        ))}
      </div>

      {isLoading ? <div className="flex justify-center py-8"><Spin /></div>
        : !notes.length ? <Empty description="No notes yet" className="py-8" />
        : (
          <div className="space-y-2">
            {notes.map((n: any) => (
              <div key={n.id} className="bg-white border border-slate-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap flex-1">{n.note}</p>
                  <Popconfirm title="Delete this note?" onConfirm={() => delNote.mutate({ nid: n.id })} okButtonProps={{ danger: true }} okText="Yes" cancelText="No">
                    <button className="text-slate-300 hover:text-red-400 flex-shrink-0 mt-0.5">
                      <Trash2 size={13} />
                    </button>
                  </Popconfirm>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Tag color={n.visibility === "client" ? "blue" : "orange"} className="text-[10px] !m-0">
                    {n.visibility === "client" ? "Client" : "Internal"}
                  </Tag>
                  {n.created_by_name && <span className="text-[10px] text-slate-400">{n.created_by_name}</span>}
                  <span className="text-[10px] text-slate-300 ml-auto">{dayjs(n.created_at).format("DD MMM HH:mm")}</span>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}

// ── Parts ─────────────────────────────────────────────────────────────────────

function PartsTab({ jobId }: { jobId: number }) {
  const { data: parts = [], isLoading } = useJobParts(jobId);

  if (isLoading) return <div className="flex justify-center py-12"><Spin /></div>;
  if (!parts.length) return <Empty description="No parts logged yet" className="py-12" />;

  const totalBillable = parts.filter((p) => p.is_billable)
    .reduce((s, p) => s + Number(p.quantity) * Number(p.unit_price), 0);

  return (
    <div>
      <div className="rounded-lg p-3 mb-4 text-center" style={{ background: "rgba(1,59,190,0.07)", border: "1px solid rgba(1,59,190,0.14)" }}>
        <div className="text-lg font-bold text-primary">£{totalBillable.toFixed(2)}</div>
        <div className="text-xs text-primary/70">Billable Parts Total</div>
      </div>
      <div className="space-y-2">
        {parts.map((p) => (
          <div key={p.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-lg p-3">
            <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center text-sm font-bold text-slate-500 flex-shrink-0">
              {Number(p.quantity)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{p.name}</p>
              <p className="text-xs text-slate-400">{Number(p.quantity)} × £{Number(p.unit_price).toFixed(2)}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-semibold text-slate-700">£{(Number(p.quantity) * Number(p.unit_price)).toFixed(2)}</div>
              {!p.is_billable && <span className="text-[10px] text-slate-400">Non-billable</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sign Off ──────────────────────────────────────────────────────────────────

function SignOffTab({ jobId }: { jobId: number }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: signOff, isLoading } = useJobSignOff(jobId);

  const save = useSaveJobSignOff({
    jobId,
    onSuccess: () => msgApi.success("Sign-off saved."),
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spin /></div>;

  if (signOff) {
    return (
      <div className="space-y-4">
        {ctx}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">✅</span>
            <span className="font-semibold text-emerald-700">Job signed off</span>
            <Tag color="green" className="ml-auto">{dayjs(signOff.signed_at).format("DD MMM YYYY HH:mm")}</Tag>
          </div>
          {signOff.customer_name && (
            <p className="text-sm text-slate-600">Customer: <strong>{signOff.customer_name}</strong></p>
          )}
          {signOff.completed_by_name && (
            <p className="text-sm text-slate-600">Completed by: {signOff.completed_by_name}</p>
          )}
        </div>
        {signOff.completion_notes && (
          <div className="bg-white border border-slate-100 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Completion Notes</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{signOff.completion_notes}</p>
          </div>
        )}
        {signOff.customer_signature_url && (
          <div className="bg-white border border-slate-100 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-2">Customer Signature</p>
            <img src={signOff.customer_signature_url} alt="Signature" className="max-h-24 border rounded" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {ctx}
      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 text-sm text-amber-700">
        No sign-off recorded yet. You can record it manually here.
      </div>
      <Form form={form} layout="vertical" size="middle"
        onFinish={(vals) => save.mutate({ customer_name: vals.customer_name, completion_notes: vals.completion_notes })}>
        <Form.Item name="customer_name" label="Customer Name">
          <Input placeholder="e.g. Jane Smith — Site Manager" />
        </Form.Item>
        <Form.Item name="completion_notes" label="Completion Notes">
          <Input.TextArea rows={4} placeholder="Describe work completed…" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={save.isPending} block>
          Record Sign-Off & Mark Complete
        </Button>
      </Form>
    </div>
  );
}

// ── Main Drawer ───────────────────────────────────────────────────────────────

interface JobDetailDrawerProps {
  job: IJob | null;
  onClose: () => void;
  onEdit: (job: IJob) => void;
  onFollowUp?: (job: IJob) => void;
}

const JobDetailDrawer = ({ job, onClose, onEdit, onFollowUp }: JobDetailDrawerProps) => {
  if (!job) return null;

  const statusCfg = getDisplayStatus(job);

  const tabs = [
    { key: "overview", label: <span className="flex items-center gap-1"><Calendar size={12} />Overview</span>, children: <OverviewTab job={job} /> },
    { key: "photos",   label: <span className="flex items-center gap-1"><Camera size={12} />Photos</span>,   children: <PhotosTab jobId={job.id} /> },
    { key: "checklist",label: <span className="flex items-center gap-1"><CheckSquare size={12} />Checklist</span>,children: <ChecklistTab jobId={job.id} /> },
    { key: "time-logs",label: <span className="flex items-center gap-1"><Clock size={12} />Time Logs</span>, children: <TimeLogsTab jobId={job.id} /> },
    { key: "notes",    label: <span className="flex items-center gap-1"><MessageSquare size={12} />Notes</span>, children: <NotesTab jobId={job.id} /> },
    { key: "parts",    label: <span className="flex items-center gap-1"><Package size={12} />Parts</span>,   children: <PartsTab jobId={job.id} /> },
    { key: "sign-off", label: <span className="flex items-center gap-1"><FileCheck2 size={12} />Sign-Off</span>,children: <SignOffTab jobId={job.id} /> },
  ];

  return (
    <Drawer
      open={!!job}
      destroyOnClose
      onClose={onClose}
      width="min(520px, 98vw)"
      styles={{ body: { padding: "16px 20px" } }}
      title={
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-800">{job.job_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>{statusCfg.label}</span>
            </div>
            <div className="text-xs text-slate-400 font-normal mt-0.5 truncate max-w-[300px]">{job.title}</div>
          </div>
          <div className="ml-auto flex gap-1.5">
            {onFollowUp && (
              <Button size="small" onClick={() => { onClose(); onFollowUp(job); }}>+ Follow-up</Button>
            )}
            <Button size="small" onClick={() => onEdit(job)}>Edit</Button>
          </div>
        </div>
      }
    >
      <Tabs defaultActiveKey="overview" size="small" items={tabs} className="job-detail-tabs" />
    </Drawer>
  );
};

export default JobDetailDrawer;
